import React, { FC } from 'react';
import styled from 'styled-components';
import { BasicButton, PrimaryButton } from '../buttons';
import { HelpIconContainer } from '../helpIconContainer';
import { HelpIcon } from '../helpers/icons';
import { SectionContainer } from './sectionContainer';
import { SectionContent } from './sectionContent';
import { SectionDescription } from './sectionDescription';
import { SectionFooter, SectionFooterLeft, SectionFooterRight } from './sectionFooter';
import { SectionHeader } from './sectionHeader';
import { SectionLink } from './sectionLink';
import { SectionTitle } from './sectionTitle';

export interface SectionAction {
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    label: string;
    primary?: boolean;
    disabled?: boolean;
}

interface CardProps {
    titleTooltip?: React.ReactElement<any> | string;
    title?: string;
    description?: React.ReactNode;
    headerActions?: React.ReactNode;
    link?: {
        label: string;
        href: string;
    };
    secondaryActions?: SectionAction[];
    actions?: SectionAction[];
    className?: string;
    id?: string;
    children?: React.ReactNode;
}

const SectionHeaderContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
`;

const SectionHeaderActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    flex-shrink: 0;
`;

const _Section: FC<CardProps> = ({ title, titleTooltip, description, headerActions, actions, link, secondaryActions, children, className, id }) => {
    const renderAction = (action: SectionAction, idx: number) => {
        const ActionButton = action.primary ? PrimaryButton : BasicButton;
        return (
            <ActionButton onClick={action.onClick} key={idx} disabled={action.disabled}>
                {action.label}
            </ActionButton>
        );
    };

    return (
        <SectionContainer className={className} id={id}>
            {(title || description || link || headerActions) && (
                <SectionHeader $hasActions={!!headerActions}>
                    <SectionHeaderContent>
                        {title && (
                            <SectionTitle>
                                {title}
                                {titleTooltip && (
                                    <HelpIconContainer tooltip={titleTooltip}>
                                        <HelpIcon />
                                    </HelpIconContainer>
                                )}
                            </SectionTitle>
                        )}

                        {description && <SectionDescription>{description}</SectionDescription>}

                        {link && (
                            <SectionLink target={'_blank'} href={link.href}>
                                {link.label}
                            </SectionLink>
                        )}
                    </SectionHeaderContent>

                    {headerActions && <SectionHeaderActions>{headerActions}</SectionHeaderActions>}
                </SectionHeader>
            )}

            <SectionContent>{children}</SectionContent>

            {((actions && actions.length > 0) || (secondaryActions && secondaryActions.length > 0)) && (
                <SectionFooter>
                    {secondaryActions && secondaryActions.length > 0 && <SectionFooterLeft>{secondaryActions.map(renderAction)}</SectionFooterLeft>}

                    {actions && actions.length > 0 && <SectionFooterRight>{actions.map(renderAction)}</SectionFooterRight>}
                </SectionFooter>
            )}
        </SectionContainer>
    );
};

export const Section = styled(_Section)``;
